/** The same pure SED projection used by generation and currency. */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { projectResearchForReport } from '../reportGeneration/researchSourceProjection';
import { projectEconomicEvidenceForReport } from '../economicContribution/sedEconomicEvidence';
import { filterKnowledgeForIntent } from '../../services/knowledge/peerReviewDb';

import type { ReportTypeId } from '../../config/reportTypes';
import type { SedSectionId } from '../../types/siteEvidence';

/**
 * Per-report-type allow-list of SED section keys. Every report type
 * surfaces siteResearch (the synthesised intro) plus a curated subset
 * relevant to its prompt template. Out-of-set sections are stripped
 * from the projection so a power-availability prompt doesn't carry
 * parcel data etc.
 */
export const PROJECTION_BY_TYPE: Record<ReportTypeId, SedSectionId[]> = {
  feasibility: [
    'climate', 'waterStress', 'seismic', 'grid', 'flood', 'environmental',
    'nuclearInfra', 'electricity', 'airQuality', 'connectivity', 'parcel',
    'regulatory', 'siteProfile', 'siteResearch', 'adamsDocuments', 'publicSentiment',
  ],
  // QA 2026-06 — `seismic` is load-bearing here: the environmental report
  // schema requires a `seismicImpact` narrative, and omitting the section
  // from the projection stripped the VERIFIED USGS data out of the SED
  // digest the model reads — producing a false "critical data gap" claim
  // that contradicted the (green) seismic radar in the same report.
  environmental: [
    'climate', 'waterStress', 'seismic', 'flood', 'environmental', 'airQuality',
    'parcel', 'siteResearch', 'publicSentiment',
  ],
  'power-availability': [
    'grid', 'electricity', 'solar', 'wind', 'nuclearInfra', 'siteResearch',
  ],
  'water-resource': [
    'climate', 'waterStress', 'flood', 'environmental', 'siteResearch',
  ],
  // P3-4 — interconnection is the NETWORK / fiber report (carrier diversity,
  // latency, route resilience), NOT a grid report. The grid / electricity /
  // nuclearInfra sections were off-topic for this type's prompt and bled
  // grid-power data into a connectivity narrative. Network-only projection:
  // keep `connectivity` + `siteResearch` + `regulatory` (fiber ROW / permitting
  // is network-relevant); strip grid / electricity / nuclearInfra. Aligns with
  // `REPORT_REQUIRED_PROVIDERS.interconnection` (already network-only:
  // `peeringdb-ix`) so the report scope is consistent end-to-end.
  interconnection: [
    'connectivity', 'siteResearch', 'regulatory',
  ],
  'supply-chain-risk': [
    'siteResearch', 'grid', 'regulatory', 'connectivity',
  ],
  'test-fit': [
    'parcel', 'siteProfile', 'grid', 'environmental', 'climate', 'flood',
    'seismic', 'siteResearch',
  ],
  // PSE-3 — include orchestrated public-sentiment aggregates for PSR prompts.
  'public-sentiment': [
    'publicSentiment', 'siteResearch', 'regulatory',
  ],
  'custom-brief': [
    'grid', 'electricity', 'regulatory', 'environmental', 'siteResearch', 'connectivity',
  ],
  'executive-summary': [
    'publicSentiment',
    'grid', 'electricity', 'environmental', 'siteResearch', 'regulatory', 'connectivity',
  ],
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. Power-economics SED
  // surface that anchors the project-finance cost basis (grid / electricity /
  // nuclearInfra) + regulatory (incentive context) + siteResearch intro.
  financial: [
    'grid', 'electricity', 'nuclearInfra', 'regulatory', 'siteResearch',
  ],
};

/**
 * Optional site-diligence sections. They are report-scoped when present but
 * do not change completeness for legacy SEDs that predate the program.
 */
const OPTIONAL_PROJECTION_BY_TYPE: Partial<Record<ReportTypeId, readonly string[]>> = {
  feasibility: [
    'topography', 'geotechnical', 'survey', 'mineralResources', 'culturalResources', 'economicEvidence',
  ],
  environmental: [
    'topography', 'geotechnical', 'mineralResources', 'culturalResources', 'economicEvidence',
  ],
  'test-fit': [
    'topography', 'geotechnical', 'survey', 'mineralResources', 'culturalResources',
  ],
  'custom-brief': [
    'topography', 'geotechnical', 'survey', 'mineralResources', 'culturalResources', 'economicEvidence',
  ],
  'executive-summary': [
    'topography', 'geotechnical', 'survey', 'mineralResources', 'culturalResources', 'economicEvidence',
  ],
  'power-availability': ['economicEvidence'],
  'water-resource': ['economicEvidence'],
  'supply-chain-risk': ['economicEvidence'],
  'public-sentiment': ['economicEvidence'],
  financial: ['economicEvidence'],
};

/**
 * SED-PROJECTION-OVERHAUL §3 — the GEN-TIME projection seam.
 *
 * `sedToText` also reads many NON-section top-level fields
 * (`benchmarks`, `projectIntent`, `siteContext`,
 * `financialAssumptions`, `coverageMode`, `supplyChainSnapshot`,
 * `externalLiterature`, `knowledgeBase`, …), so a projection that kept
 * only allow-listed keys would silently delete all of those and shrink
 * every prompt.
 *
 * This helper strips only the sections that are ENUMERATED in at least
 * one `PROJECTION_BY_TYPE` / `OPTIONAL_PROJECTION_BY_TYPE` list but
 * excluded from THIS report type's list. Sections that no list
 * enumerates are "universal" and always preserved: `externalLiterature`,
 * `technology`, `onSiteInfrastructure` and the BTM grounding sections
 * (`cooling`, `power`, `firmCapacity`, `lcoe`, `fuelSupply`), which
 * every report type needs. Adding one of them to a list would strip it
 * from every other type's prompt. Every non-section field `sedToText`
 * consumes is preserved too. Completeness is left untouched (the prompt
 * header reports the full-SED completeness, which is the honest
 * denominator for the LLM).
 *
 * Pure function — does not mutate the input. Unknown report types fall
 * back to the feasibility allow-list.
 */
export interface PromptEvidenceProjectionOptions {
  /**
   * Per-report selected document IDs (`userIncluded: true` / non-`ai-only`).
   * When supplied, strips excluded `uploadedDocuments`, `documentFacts`, and
   * uncorroborated `verified-from-doc` section slots from the prompt projection
   * while leaving the canonical SED object untouched.
   */
  selectedDocIds?: ReadonlySet<string>;
  /** Optional selected document file names for legacy entries lacking `docId`. */
  selectedDocNames?: ReadonlySet<string>;
}

/**
 * Filter a prompt-projected SED to only the documents selected for this report
 * (`selectedDocIds` / `selectedDocNames`). Pure — never mutates the input.
 */
export function projectSiteEvidenceForSelectedDocuments(
  sed: SiteEvidenceDocument,
  selectedDocIds: ReadonlySet<string>,
  selectedDocNames?: ReadonlySet<string>,
): SiteEvidenceDocument {
  const projected: Record<string, unknown> = { ...(sed as unknown as Record<string, unknown>) };

  const isDocIncluded = (docId?: string, fileName?: string): boolean => {
    if (typeof docId === 'string' && docId.length > 0) {
      return selectedDocIds.has(docId);
    }
    if (typeof fileName === 'string' && fileName.length > 0 && selectedDocNames) {
      return selectedDocNames.has(fileName);
    }
    return false;
  };

  const excludedDocNames = new Set<string>();
  if (Array.isArray(sed.uploadedDocuments)) {
    for (const doc of sed.uploadedDocuments) {
      if (!isDocIncluded(doc.docId, doc.fileName) && typeof doc.fileName === 'string' && doc.fileName.length > 0) {
        excludedDocNames.add(doc.fileName);
      }
    }
    projected.uploadedDocuments = sed.uploadedDocuments.filter(doc => isDocIncluded(doc.docId, doc.fileName));
  }

  if (Array.isArray(sed.documentFacts)) {
    projected.documentFacts = sed.documentFacts.filter(fact =>
      isDocIncluded(fact.documentId, fact.documentName),
    );
  }

  for (const [key, value] of Object.entries(projected)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const sec = value as {
      status?: string;
      data?: unknown;
      source?: string;
      provenanceDetail?: {
        sourceType?: string;
        supportingDocIds?: string[];
        linkedDocumentIds?: string[];
        linkedDocumentRefs?: Array<{ docId: string; fileName?: string; rationale?: string }>;
        [k: string]: unknown;
      };
      [k: string]: unknown;
    };
    if (typeof sec.status !== 'string') continue;

    const pd = sec.provenanceDetail;
    const rawLinkedRefs = Array.isArray(pd?.linkedDocumentRefs)
      ? pd.linkedDocumentRefs.filter(
          (ref): ref is { docId: string; fileName?: string; rationale?: string } =>
            Boolean(ref && typeof ref === 'object'),
        )
      : [];
    const supportingIds = [
      ...(Array.isArray(pd?.supportingDocIds) ? pd.supportingDocIds : []),
      ...(Array.isArray(pd?.linkedDocumentIds) ? pd.linkedDocumentIds : []),
      ...rawLinkedRefs.map(r => r.docId),
    ].filter((id): id is string => typeof id === 'string' && id.length > 0);
    const linkedRefFileNames = rawLinkedRefs
      .map(r => r.fileName)
      .filter((name): name is string => typeof name === 'string' && name.length > 0);

    const src = typeof sec.source === 'string' ? sec.source : '';
    const matchesSelectedName = Boolean(
      selectedDocNames &&
        ([...selectedDocNames].some(name => name.length > 0 && src.includes(name)) ||
          linkedRefFileNames.some(name => selectedDocNames.has(name))),
    );
    const matchesExcludedName =
      [...excludedDocNames].some(name => name.length > 0 && src.includes(name)) ||
      linkedRefFileNames.some(name => excludedDocNames.has(name));

    const keptSupportingIds = supportingIds.filter(id => selectedDocIds.has(id));
    const hasPositiveSelectedDocProvenance = keptSupportingIds.length > 0 || matchesSelectedName;

    const isDocOnlySection =
      sec.status === 'verified-from-doc' ||
      pd?.sourceType === 'doc' ||
      pd?.sourceType === 'doc-override';

    const isMixedSectionTaintedOnlyByExcludedDocs =
      !isDocOnlySection &&
      !hasPositiveSelectedDocProvenance &&
      (supportingIds.length > 0 || rawLinkedRefs.length > 0 || matchesExcludedName);

    if ((isDocOnlySection && !hasPositiveSelectedDocProvenance) || isMixedSectionTaintedOnlyByExcludedDocs) {
      projected[key] = {
        ...sec,
        status: 'unavailable',
        data: null,
        ...(typeof sec.source === 'string' && matchesExcludedName ? { source: 'unavailable' } : {}),
        ...(pd
          ? {
              provenanceDetail: {
                ...pd,
                ...(Array.isArray(pd.supportingDocIds) ? { supportingDocIds: [] } : {}),
                ...(Array.isArray(pd.linkedDocumentIds) ? { linkedDocumentIds: [] } : {}),
                ...(Array.isArray(pd.linkedDocumentRefs) ? { linkedDocumentRefs: [] } : {}),
              },
            }
          : {}),
      };
      continue;
    }

    if (pd && (supportingIds.length > 0 || rawLinkedRefs.length > 0)) {
      projected[key] = {
        ...sec,
        provenanceDetail: {
          ...pd,
          ...(Array.isArray(pd.supportingDocIds)
            ? {
                supportingDocIds: pd.supportingDocIds.filter(
                  (id): id is string => typeof id === 'string' && selectedDocIds.has(id),
                ),
              }
            : {}),
          ...(Array.isArray(pd.linkedDocumentIds)
            ? {
                linkedDocumentIds: pd.linkedDocumentIds.filter(
                  (id): id is string => typeof id === 'string' && selectedDocIds.has(id),
                ),
              }
            : {}),
          ...(Array.isArray(pd.linkedDocumentRefs)
            ? {
                linkedDocumentRefs: rawLinkedRefs.filter(ref =>
                  isDocIncluded(ref.docId, ref.fileName),
                ),
              }
            : {}),
        },
      };
    }
  }

  return projected as unknown as SiteEvidenceDocument;
}

export function projectSiteEvidenceForPrompt(
  sed: SiteEvidenceDocument,
  reportType: ReportTypeId,
  options?: PromptEvidenceProjectionOptions,
): SiteEvidenceDocument {
  const knownProjection = PROJECTION_BY_TYPE[reportType];
  const baseProjection = knownProjection ?? PROJECTION_BY_TYPE.feasibility;
  const optionalProjection = knownProjection
    ? OPTIONAL_PROJECTION_BY_TYPE[reportType] ?? []
    : OPTIONAL_PROJECTION_BY_TYPE.feasibility ?? [];
  const keep = new Set<string>([...baseProjection, ...optionalProjection]);

  // Universe of sections any report type tailors on. Only these are
  // candidates for stripping; everything else is universal / always-on.
  const tailorableSections = new Set<string>();
  for (const ids of Object.values(PROJECTION_BY_TYPE)) {
    for (const id of ids) tailorableSections.add(id);
  }
  for (const ids of Object.values(OPTIONAL_PROJECTION_BY_TYPE)) {
    for (const id of ids ?? []) tailorableSections.add(id);
  }

  const projected: Record<string, unknown> = { ...(sed as unknown as Record<string, unknown>) };
  if (sed.siteResearch) projected.siteResearch = projectResearchForReport(sed.siteResearch);

  if ((sed.fullProjectIntent?.nuclearInterest ?? sed.projectIntent?.nuclearInterest) === 'no') {
    // A legacy stored SED can predate the knowledge-base array. Leave the
    // field exactly as stored (absent stays absent, never an invented `[]`),
    // so projection cannot throw and the material hashes of KB-bearing SEDs
    // are unchanged.
    if (Array.isArray(sed.knowledgeBase)) {
      projected.knowledgeBase = filterKnowledgeForIntent(sed.knowledgeBase, 'no');
    }
    delete projected.adamsDocuments;
  }

  for (const key of Object.keys(projected)) {
    // Strip a section only when it's a tailorable section AND not kept
    // for this type. Non-tailorable keys (universal sections + all
    // metadata / non-section fields) always survive.
    if (tailorableSections.has(key) && !keep.has(key)) {
      delete projected[key];
    }
  }


  const economicEvidence = projected.economicEvidence as SiteEvidenceDocument['economicEvidence'];
  if (economicEvidence) {
    const scoped = projectEconomicEvidenceForReport(economicEvidence, reportType);
    if (scoped) projected.economicEvidence = scoped;
    else delete projected.economicEvidence;
  }

  const typeProjected = projected as unknown as SiteEvidenceDocument;
  if (options?.selectedDocIds) {
    return projectSiteEvidenceForSelectedDocuments(
      typeProjected,
      options.selectedDocIds,
      options.selectedDocNames,
    );
  }
  return typeProjected;
}
